import { useState } from 'react';
import { GitMerge, Cloud, AlertCircle } from 'lucide-react';
import { resolveGuestMigrationDecision } from '../db/guestMigration';
import { useStore } from '../store/useStore';

interface GuestMigrationModalProps {
  userId: string;
  localCount: number;
  remoteCount: number;
  onResolved: () => void;
}

export function GuestMigrationModal({ userId, localCount, remoteCount, onResolved }: GuestMigrationModalProps) {
  const [loading, setLoading] = useState(false);
  const loadData = useStore(s => s.loadData);

  const handleDecision = async (decision: 'merge' | 'discard') => {
    setLoading(true);
    try {
      await resolveGuestMigrationDecision(userId, decision);
      await loadData();
      onResolved();
    } catch (err) {
      console.error('Error resolving guest migration decision:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4 font-sans select-none animate-in fade-in duration-200">
      <div className="bg-white max-w-lg w-full rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-6 space-y-5 text-left">
        
        {/* Encabezado */}
        <div className="flex items-start gap-3.5">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 border border-amber-200/80 flex items-center justify-center shrink-0">
            <AlertCircle size={20} />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-bold text-slate-900 tracking-tight">
              Encontramos datos locales de invitado
            </h2>
            <p className="text-xs text-slate-500 leading-relaxed">
              Tenés <strong className="text-slate-800 font-semibold">{localCount} asignaturas</strong> guardadas como invitado en este dispositivo y <strong className="text-slate-800 font-semibold">{remoteCount} asignaturas</strong> en tu cuenta de la nube.
            </p>
          </div>
        </div>

        {/* Indicador de estrategia */}
        <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200/70 text-xs text-slate-600 space-y-1">
          <span className="font-semibold text-slate-800 block">¿Cómo querés proceder?</span>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Podés fusionar las materias de este dispositivo con las de tu cuenta o descartar los datos locales para quedarte únicamente con los de la nube.
          </p>
        </div>

        {/* Botones de Acción */}
        <div className="space-y-2.5 pt-1">
          <button
            disabled={loading}
            onClick={() => handleDecision('merge')}
            className="w-full bg-primary hover:bg-primary-container text-white text-xs font-semibold px-4 py-3 rounded-xl transition-all shadow-2xs flex items-center justify-between group active:scale-[0.98] cursor-pointer disabled:opacity-50"
          >
            <div className="flex items-center gap-2">
              <GitMerge size={16} />
              <span>Fusionar datos (Recomendado)</span>
            </div>
            <span className="text-[10px] font-mono opacity-80 group-hover:translate-x-0.5 transition-transform">
              Conserva todo →
            </span>
          </button>

          <button
            disabled={loading}
            onClick={() => handleDecision('discard')}
            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold px-4 py-3 rounded-xl transition-all flex items-center justify-between group cursor-pointer disabled:opacity-50"
          >
            <div className="flex items-center gap-2">
              <Cloud size={16} className="text-slate-500" />
              <span>Descartar datos locales y usar solo la nube</span>
            </div>
            <span className="text-[10px] font-mono text-slate-400 group-hover:translate-x-0.5 transition-transform">
              Solo nube →
            </span>
          </button>
        </div>

      </div>
    </div>
  );
}
